import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/Button'

const categories = [
  { name: 'Programming', slug: 'programming' },
  { name: 'Web Development', slug: 'web-development' },
  { name: 'Frontend', slug: 'frontend' },
  { name: 'Backend', slug: 'backend' },
  { name: 'DevOps', slug: 'devops' },
  { name: 'Cloud', slug: 'cloud' },
  { name: 'Security', slug: 'security' },
  { name: 'Data', slug: 'data' },
]

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isCategoryOpen, setIsCategoryOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location])

  const isActive = (path: string) => location.pathname === path

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/95 backdrop-blur-lg border-b border-white/10' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-4 group"> 
              <div className="relative">
                <div className="absolute -inset-2 bg-cyan-500/10 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                <img 
                  src="/Apex Software Technologies.png" 
                  alt="Apex Software Technologies" 
                  className="h-10 w-auto relative object-contain"
                />
              </div>
              <div className="flex flex-col border-l border-white/10 pl-4">
                <span className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.2em] leading-none mb-1">Matrix</span>
                <span className="text-[9px] text-white/40 font-mono tracking-tighter leading-none uppercase">Technical Stream</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              <a href="https://apexsofteck.com" className="mr-2">
                <Button variant="outline" size="sm" className="bg-cyan-500/10 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20">
                  Main Site
                </Button>
              </a>
              <Link to="/">
                <Button variant={isActive('/') ? 'default' : 'ghost'} size="sm">
                  Home
                </Button>
              </Link>
              
              {/* Categories Dropdown */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                  className="flex items-center space-x-1"
                >
                  Categories
                  <ChevronDown className={`w-4 h-4 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} />
                </Button>
                
                <AnimatePresence>
                  {isCategoryOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-56 py-2 bg-black/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-xl"
                    >
                      {categories.map((category) => (
                        <Link
                          key={category.slug}
                          to={`/category/${category.slug}`}
                          className="block px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                        >
                          {category.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link to="/blog">
                <Button variant={location.pathname.startsWith('/blog') ? 'default' : 'ghost'} size="sm">
                  All Posts
                </Button>
              </Link>
              
              <Link to="/about">
                <Button variant={isActive('/about') ? 'default' : 'ghost'} size="sm">
                  About
                </Button>
              </Link>
            </div>

            {/* CTA & Mobile Menu */}
            <div className="flex items-center space-x-4">
              <Link to="/contact" className="hidden sm:block">
                <Button size="sm">Contact Us</Button>
              </Link>
              
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-black/95 backdrop-blur-xl pt-20">
              <div className="px-4 py-8 space-y-4">
                <Link to="/" className="block text-2xl font-semibold text-white py-2">Home</Link>
                <a href="https://apexsofteck.com" className="block text-2xl font-semibold text-cyan-400 py-2">Main Site</a>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-sm text-white/50 mb-3">Categories</p>
                  {categories.map((category) => (
                    <Link
                      key={category.slug}
                      to={`/category/${category.slug}`}
                      className="block text-lg text-white/80 hover:text-white py-2"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
                <Link to="/blog" className="block text-2xl font-semibold text-white py-2 border-t border-white/10">All Posts</Link>
                <Link to="/about" className="block text-2xl font-semibold text-white py-2">About</Link>
                <Link to="/contact" className="block text-2xl font-semibold text-white py-2">Contact</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
